import moment from "moment";

import { DEFAULT_OVERRIDE, OVERRIDE_OPERATORS } from "../consts/overrides";
import { OVERRIDES_PLAN_TYPE } from "../consts/planTypes";

import { processRoutes, processRoutesProfile } from "./routes";
import {
  getTimestampFromHHMM,
  getTimestampFromDate,
  isTimeGreaterBy,
} from "./date";
import { copyOverlayDetails } from "./volumeData";

export const loadOverrideType = async ({
  routes,
  matrixProfile,
  volumeData,
  lookupTables,
}) => {
  routes = processRoutes(routes, matrixProfile.routeConfig);

  const overlayData = volumeData && {
    overlayProfile: { ...volumeData.overlayProfile },
    overlayDetails: copyOverlayDetails(volumeData.overlayDetails),
  };

  const {
    routesWithVolume,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    processedVolumeData,
  } = await processRoutesProfile({
    routeConfig: matrixProfile.routeConfig,
    redirectConfig: matrixProfile.redirectConfig,
    routes,
    volumeData: overlayData,
    overrideAreas: matrixProfile.overrideConfig,
    pickupAreas: matrixProfile.pickupConfig,
    locationConfig: matrixProfile.locationConfig,
    lookupTables,
    planType: OVERRIDES_PLAN_TYPE,
  });

  return {
    ...matrixProfile,
    overrideAreas: matrixProfile.overrideConfig,
    routes: routesWithVolume,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    volumeData: processedVolumeData,
  };
};

export const updateOverrideFromLevel = ({
  overrideAreas,
  newOverrides,
  editOverrides,
  deletedOverrides,
}) => {
  const { level: editLevel } =
    Object.values(newOverrides)[0] ||
    Object.values(editOverrides)[0] ||
    Object.values(deletedOverrides)[0];

  const updatedLevelOverrides = overrideAreas[editLevel.toLowerCase()]
    .reduce((acc, override) => {
      if (deletedOverrides[override.overrideUuid]) return acc;
      else if (editOverrides[override.overrideUuid])
        acc.push(editOverrides[override.overrideUuid]);
      else acc.push(override);
      return acc;
    }, [])
    .concat(Object.values(newOverrides));

  return {
    ...overrideAreas,
    [editLevel.toLowerCase()]: getOverridesWithConflicts(updatedLevelOverrides),
  };
};

export const getRecalculatedOverride = (override) => {
  const recalculatedOverride = { ...override };

  const recalculateOverrideTimes = (
    time,
    operator,
    earliestFieldName,
    latestFieldName
  ) => {
    if (!time) {
      recalculatedOverride[earliestFieldName] =
        DEFAULT_OVERRIDE[earliestFieldName];
      recalculatedOverride[latestFieldName] = DEFAULT_OVERRIDE[latestFieldName];
      return;
    }
    switch (operator) {
      case OVERRIDE_OPERATORS.lessThenOrEqual:
        recalculatedOverride[earliestFieldName] = getTimestampFromHHMM("00:00");
        recalculatedOverride[latestFieldName] = time;
        break;
      case OVERRIDE_OPERATORS.greaterThenOrEqual:
        recalculatedOverride[earliestFieldName] = time;
        recalculatedOverride[latestFieldName] = getTimestampFromHHMM("23:59");
        break;
      case OVERRIDE_OPERATORS.lessThen:
        recalculatedOverride[earliestFieldName] = getTimestampFromHHMM("00:00");
        recalculatedOverride[latestFieldName] = getTimestampFromDate(
          new Date(+new Date(time) - 1000)
        );
        break;
      case OVERRIDE_OPERATORS.greaterThen:
        recalculatedOverride[earliestFieldName] = getTimestampFromDate(
          new Date(+new Date(time) + 1000)
        );
        recalculatedOverride[latestFieldName] = getTimestampFromHHMM("23:59");
        break;
      default:
        recalculatedOverride[earliestFieldName] = time;
        recalculatedOverride[latestFieldName] = time;
        break;
    }
  };

  recalculateOverrideTimes(
    override.uiOverrideStartTime,
    override.startOperator,
    "startEarliestTime",
    "startLatestTime"
  );
  recalculateOverrideTimes(
    override.uiOverrideEndTime,
    override.endOperator,
    "endEarliestTime",
    "endLatestTime"
  );
  return recalculatedOverride;
};

export const clearOverrideStartTimes = (override) => {
  override.startOperator = DEFAULT_OVERRIDE.startOperator;
  override.uiOverrideStartTime = DEFAULT_OVERRIDE.uiOverrideStartTime;
  override.startEarliestTime = DEFAULT_OVERRIDE.startEarliestTime;
  override.startLatestTime = DEFAULT_OVERRIDE.startLatestTime;
};

export const clearOverrideEndTimes = (override) => {
  override.endOperator = DEFAULT_OVERRIDE.endOperator;
  override.uiOverrideEndTime = DEFAULT_OVERRIDE.uiOverrideEndTime;
  override.endEarliestTime = DEFAULT_OVERRIDE.endEarliestTime;
  override.endLatestTime = DEFAULT_OVERRIDE.endLatestTime;
};

export const getOverridesRouteCodesMap = (overrideAreas) => {
  const routeCodesMap = {};
  Object.keys(overrideAreas).forEach((level) => {
    overrideAreas[level].forEach(({ routeCode }) => {
      routeCodesMap[routeCode] = true;
    });
  });
  return routeCodesMap;
};

export const getOverrideMap = (overrideAreas) =>
  Object.keys(overrideAreas || {}).reduce((acc, areaType) => {
    overrideAreas[areaType].forEach((override) => {
      const { outer, sector, postcode } = override;

      if (postcode && !acc[postcode]) acc[postcode] = [];
      if (postcode) return acc[postcode].push(override);

      if (sector && !acc[sector]) acc[sector] = [];
      if (sector) return acc[sector].push(override);

      if (!acc[outer]) acc[outer] = [];
      acc[outer].push(override);
    });
    return acc;
  }, {});

export const getOverrideForStopService = (
  overrides = [],
  { serviceStartTime, serviceEndTime }
) => {
  const isHourOrLess = !isTimeGreaterBy(serviceStartTime, serviceEndTime, 61);
  serviceStartTime = getTimestampFromHHMM(serviceStartTime);
  serviceEndTime = getTimestampFromHHMM(serviceEndTime);

  return overrides
    .filter(
      ({
        startEarliestTime,
        startLatestTime,
        endEarliestTime,
        endLatestTime,
      }) =>
        serviceStartTime >= startEarliestTime &&
        serviceStartTime <= startLatestTime &&
        serviceEndTime >= endEarliestTime &&
        serviceEndTime <= endLatestTime
    )
    .find(({ excludeOneHour }) => !excludeOneHour || !isHourOrLess);
};

export const getOverridesConflictsAreas = ({ overrideAreas }) => {
  const overridesConflictsPostcodes = Object.keys(overrideAreas).reduce(
    (acc, areaKey) => {
      overrideAreas[areaKey].forEach(
        ({ outer, sector, postcode, conflict }) =>
          conflict && acc.add(postcode || sector || outer)
      );
      return acc;
    },
    new Set()
  );
  return Array.from(overridesConflictsPostcodes);
};

export const getOverridesWithConflicts = (overrides) =>
  overrides.reduce((acc, override) => {
    const hasConflict = overrides.some((otherOverride) => {
      return isOverrideConflict(override, otherOverride);
    });
    if (override.conflict === hasConflict) acc.push(override);
    else acc.push({ ...override, conflict: hasConflict });
    return acc;
  }, []);

export const isOverrideConflict = (override, otherOverride) => {
  const {
    outer,
    sector,
    postcode,
    overrideUuid,
    routeCode,
    uiOverrideStartTime,
    uiOverrideEndTime,
    startOperator,
    endOperator,
    startEarliestTime,
    startLatestTime,
    endEarliestTime,
    endLatestTime,
    excludeOneHour,
  } = override;

  const {
    outer: otherOuter,
    sector: otherSector,
    postcode: otherPostcode,
    overrideUuid: otherOverrideUuid,
    routeCode: otherRouteCode,
    uiOverrideStartTime: otherUiOverrideStartTime,
    uiOverrideEndTime: otherUiOverrideEndTime,
    startOperator: otherStartOperator,
    endOperator: otherEndOperator,
    startEarliestTime: otherStartEarliestTime,
    startLatestTime: otherStartLatestTime,
    endEarliestTime: otherEndEarliestTime,
    endLatestTime: otherEndLatestTime,
    excludeOneHour: otherExcludeOneHour,
  } = otherOverride;

  let conflict = false;

  if (
    outer !== otherOuter ||
    sector !== otherSector ||
    postcode !== otherPostcode ||
    overrideUuid === otherOverrideUuid
  )
    return false;
  else if (routeCode === otherRouteCode)
    conflict = !!(
      uiOverrideStartTime === otherUiOverrideStartTime &&
      uiOverrideEndTime === otherUiOverrideEndTime &&
      startOperator === otherStartOperator &&
      endOperator === otherEndOperator
    );
  else if (
    [startOperator, endOperator, otherStartOperator, otherEndOperator].every(
      (operator) => operator === OVERRIDE_OPERATORS.equal
    )
  )
    conflict = !!(
      startEarliestTime === otherStartEarliestTime &&
      startLatestTime === otherStartLatestTime &&
      endEarliestTime === otherEndEarliestTime &&
      endLatestTime === otherEndLatestTime
    );
  else if (
    startEarliestTime === otherStartEarliestTime &&
    startLatestTime === otherStartLatestTime &&
    startOperator &&
    startOperator === otherStartOperator
  )
    conflict = true;
  else if (
    startEarliestTime === startLatestTime &&
    (startEarliestTime < otherStartEarliestTime ||
      startEarliestTime > otherEndLatestTime)
  )
    return false;
  else if (
    otherStartEarliestTime === otherStartLatestTime &&
    (otherStartEarliestTime < startEarliestTime ||
      otherStartEarliestTime > endLatestTime)
  )
    return false;
  else if (
    endEarliestTime === endLatestTime &&
    (endEarliestTime < otherStartEarliestTime ||
      endEarliestTime > otherEndLatestTime)
  )
    return false;
  else if (
    otherEndEarliestTime === otherEndLatestTime &&
    (otherEndEarliestTime < startEarliestTime ||
      otherEndEarliestTime > endLatestTime)
  )
    return false;
  else if (
    startOperator &&
    otherStartOperator &&
    startEarliestTime <= otherStartEarliestTime &&
    startLatestTime >= otherStartEarliestTime
  )
    conflict = true;
  else if (
    startOperator &&
    otherStartOperator &&
    startEarliestTime >= otherStartEarliestTime &&
    startEarliestTime <= otherStartLatestTime
  )
    conflict = true;
  else if (
    endOperator &&
    otherEndOperator &&
    endEarliestTime <= otherEndEarliestTime &&
    endLatestTime >= otherEndEarliestTime
  )
    conflict = true;
  else
    conflict = !!(
      endOperator &&
      otherEndOperator &&
      endEarliestTime >= otherEndEarliestTime &&
      endEarliestTime <= otherEndLatestTime
    );

  if (
    conflict &&
    excludeOneHour &&
    moment(otherEndLatestTime).diff(
      moment(otherStartEarliestTime),
      "minutes"
    ) <= 60
  )
    return false;
  else if (
    conflict &&
    otherExcludeOneHour &&
    moment(endLatestTime).diff(moment(startEarliestTime), "minutes") <= 60
  )
    return false;
  else return conflict;
};
